import ReactGA from 'react-ga4';

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-WHW7V4JD04';

// Initialize Google Analytics
export const initGA = () => {
  if (typeof window !== 'undefined') {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      gaOptions: {
        cookieFlags: 'SameSite=None;Secure',
        cookieDomain: 'auto',
      },
      testMode: process.env.REACT_APP_ENV !== 'production'
    });
    console.log(`Google Analytics initialized with ID: ${GA_MEASUREMENT_ID}`);
  }
};

// Track page view
export const trackPageView = (path) => {
  const page = path || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '');
  if (page) {
    ReactGA.send({
      hitType: 'pageview',
      page: page
    });
    console.log(`Page view tracked: ${page}`);
  }
};

// Track custom event
export const trackEvent = (category, action, label, value) => {
  ReactGA.event({
    category,
    action,
    label,
    value,
  });
  console.log(`Event tracked: ${category} - ${action} - ${label}`);
};

// ============ E-COMMERCE TRACKING ============

// View Item (Product Detail Page)
export const trackViewItem = (product) => {
  if (!product) return;

  ReactGA.event("view_item", {
    currency: "INR",
    value: product.wholesalePrice || product.price,
    items: [
      {
        item_id: product._id,
        item_name: product.name || product.title,
        item_category: Array.isArray(product.category) ? product.category[0] : product.category || "Unknown",
        price: product.wholesalePrice || product.price,
        quantity: 1
      }
    ]
  });
  console.log(`Product view tracked: ${product.name || product.title} - ₹${product.wholesalePrice || product.price}`);
};

// Backward compatibility
export const trackProductView = trackViewItem;

// View Item List (Product Grid/Category Pages)
export const trackViewItemList = (products, listName = "Product Grid") => {
  if (!products || products.length === 0) return;

  ReactGA.event("view_item_list", {
    item_list_name: listName,
    items: products.slice(0, 20).map((product, index) => ({
      item_id: product._id,
      item_name: product.name || product.title,
      item_category: Array.isArray(product.category) ? product.category[0] : product.category || "Unknown",
      price: product.wholesalePrice || product.price,
      index: index
    }))
  });
  console.log(`Item list viewed: ${listName} - ${products.length} items`);
};

// Select Item (Click on Product)
export const trackSelectItem = (product, listName = "Product Grid") => {
  if (!product) return;

  ReactGA.event("select_item", {
    item_list_name: listName,
    items: [
      {
        item_id: product._id,
        item_name: product.name || product.title,
        item_category: Array.isArray(product.category) ? product.category[0] : product.category || "Unknown",
        price: product.wholesalePrice || product.price
      }
    ]
  });
  console.log(`Item selected: ${product.name || product.title}`);
};

// Add to Cart
export const trackAddToCart = (product, quantity = 1) => {
  if (!product) return;

  const price = product.wholesalePrice || product.price;
  ReactGA.event("add_to_cart", {
    currency: "INR",
    value: price * quantity,
    items: [
      {
        item_id: product._id,
        item_name: product.name || product.title,
        item_category: Array.isArray(product.category) ? product.category[0] : product.category || "Unknown",
        price: price,
        quantity: quantity
      }
    ]
  });
  console.log(`Add to cart tracked: ${product.name || product.title} - ${quantity}x ₹${price}`);
};

// Remove from Cart
export const trackRemoveFromCart = (product, quantity = 1) => {
  if (!product) return;

  const price = product.wholesalePrice || product.price;
  ReactGA.event("remove_from_cart", {
    currency: "INR",
    value: price * quantity,
    items: [
      {
        item_id: product._id,
        item_name: product.name || product.title,
        item_category: Array.isArray(product.category) ? product.category[0] : product.category || "Unknown",
        price: price,
        quantity: quantity
      }
    ]
  });
  console.log(`Remove from cart tracked: ${product.name || product.title}`);
};

// View Cart
export const trackViewCart = (cartItems) => {
  if (!cartItems || cartItems.length === 0) return;

  const totalValue = cartItems.reduce((sum, item) => {
    const price = item.wholesalePrice || item.price;
    return sum + (price * item.quantity);
  }, 0);

  ReactGA.event("view_cart", {
    currency: "INR",
    value: totalValue,
    items: cartItems.map(item => ({
      item_id: item._id,
      item_name: item.name || item.title,
      item_category: Array.isArray(item.category) ? item.category[0] : item.category || "Unknown",
      price: item.wholesalePrice || item.price,
      quantity: item.quantity
    }))
  });
  console.log(`Cart viewed: ${cartItems.length} items - ₹${totalValue}`);
};

// Begin Checkout
export const trackBeginCheckout = (cartItems) => {
  if (!cartItems || cartItems.length === 0) return;

  const totalValue = cartItems.reduce((sum, item) => {
    const price = item.wholesalePrice || item.price;
    return sum + (price * item.quantity);
  }, 0);

  ReactGA.event("begin_checkout", {
    currency: "INR",
    value: totalValue,
    items: cartItems.map(item => ({
      item_id: item._id,
      item_name: item.name || item.title,
      item_category: Array.isArray(item.category) ? item.category[0] : item.category || "Unknown",
      price: item.wholesalePrice || item.price,
      quantity: item.quantity
    }))
  });
  console.log(`Checkout started: ${cartItems.length} items - ₹${totalValue}`);
};

// Add Shipping Info
export const trackAddShippingInfo = (cartItems, shippingInfo = {}) => {
  if (!cartItems || cartItems.length === 0) return;

  const totalValue = cartItems.reduce((sum, item) => {
    const price = item.wholesalePrice || item.price;
    return sum + (price * item.quantity);
  }, 0);

  ReactGA.event("add_shipping_info", {
    currency: "INR",
    value: totalValue,
    shipping_tier: shippingInfo.method || "Standard",
    items: cartItems.map(item => ({
      item_id: item._id,
      item_name: item.name || item.title,
      item_category: Array.isArray(item.category) ? item.category[0] : item.category || "Unknown",
      price: item.wholesalePrice || item.price,
      quantity: item.quantity
    }))
  });
  console.log(`Shipping info added: ${shippingInfo.method || "Standard"}`);
};

// Add Payment Info
export const trackAddPaymentInfo = (cartItems, paymentType = "cash_on_delivery") => {
  if (!cartItems || cartItems.length === 0) return;

  const totalValue = cartItems.reduce((sum, item) => {
    const price = item.wholesalePrice || item.price;
    return sum + (price * item.quantity);
  }, 0);

  ReactGA.event("add_payment_info", {
    currency: "INR",
    value: totalValue,
    payment_type: paymentType,
    items: cartItems.map(item => ({
      item_id: item._id,
      item_name: item.name || item.title,
      item_category: Array.isArray(item.category) ? item.category[0] : item.category || "Unknown",
      price: item.wholesalePrice || item.price,
      quantity: item.quantity
    }))
  });
  console.log(`Payment info added: ${paymentType}`);
};

// Purchase (Order Completion)
export const trackPurchase = (orderId, cartItems, totalAmount, tax = 0, shipping = 0) => {
  if (!cartItems || cartItems.length === 0) return;

  ReactGA.event("purchase", {
    transaction_id: orderId,
    currency: "INR",
    value: totalAmount,
    tax: tax,
    shipping: shipping,
    items: cartItems.map(item => ({
      item_id: item._id,
      item_name: item.name || item.title,
      item_category: Array.isArray(item.category) ? item.category[0] : item.category || "Unknown",
      price: item.wholesalePrice || item.price,
      quantity: item.quantity
    }))
  });
  console.log(`Purchase tracked: ${orderId} - ₹${totalAmount}`);
};

// ============ USER ENGAGEMENT ============

// Search
export const trackSearch = (searchTerm) => {
  if (!searchTerm) return;

  ReactGA.event("search", {
    search_term: searchTerm
  });
  console.log(`Search tracked: ${searchTerm}`);
};

// Add to Wishlist
export const trackAddToWishlist = (product) => {
  if (!product) return;

  const price = product.wholesalePrice || product.price;
  ReactGA.event("add_to_wishlist", {
    currency: "INR",
    value: price,
    items: [
      {
        item_id: product._id,
        item_name: product.name || product.title,
        item_category: Array.isArray(product.category) ? product.category[0] : product.category || "Unknown",
        price: price
      }
    ]
  });
  console.log(`Wishlist add tracked: ${product.name || product.title}`);
};

// Remove from Wishlist
export const trackRemoveFromWishlist = (product) => {
  if (!product) return;

  trackEvent('Wishlist', 'Remove', product.name || product.title);
  console.log(`Wishlist remove tracked: ${product.name || product.title}`);
};

// Sign Up
export const trackSignUp = (method = "email") => {
  ReactGA.event("sign_up", {
    method: method
  });
  console.log(`Sign up tracked: ${method}`);
};

// Login
export const trackLogin = (method = "email") => {
  ReactGA.event("login", {
    method: method
  });
  console.log(`Login tracked: ${method}`);
};

// Share
export const trackShare = (contentType, itemId) => {
  ReactGA.event("share", {
    content_type: contentType,
    item_id: itemId
  });
  console.log(`Share tracked: ${contentType} - ${itemId}`);
};

// ============ FILTERING & SORTING ============

export const trackFilter = (filterType, filterValue) => {
  ReactGA.event("filter_products", {
    filter_type: filterType,
    filter_value: filterValue
  });
  console.log(`Filter tracked: ${filterType} = ${filterValue}`);
};

export const trackSort = (sortType) => {
  ReactGA.event("sort_products", {
    sort_type: sortType
  });
  console.log(`Sort tracked: ${sortType}`);
};

// ============ FORMS & INTERACTIONS ============

// Newsletter Subscription
export const trackNewsletterSubscription = (email) => {
  ReactGA.event("subscribe_newsletter", {
    method: "email"
  });
  console.log(`Newsletter subscription tracked`);
};

// Contact Form Submission
export const trackContactForm = () => {
  ReactGA.event("contact_form_submit", {
    form_type: "contact"
  });
  console.log(`Contact form submission tracked`);
};

// Form submission (generic)
export const trackFormSubmission = (formName) => {
  trackEvent('Form', 'Submit', formName);
};

// Outbound links
export const trackOutboundLink = (url, category = 'Outbound Link') => {
  ReactGA.outboundLink(
    {
      label: url,
    },
    () => {
      console.log('Outbound link tracked:', url);
    }
  );
  trackEvent(category, 'Click', url);
};

// Custom events
export const trackCustomEvent = (eventName, parameters = {}) => {
  ReactGA.event(eventName, parameters);
  console.log(`Custom event tracked: ${eventName}`, parameters);
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackViewItem,
  trackProductView,
  trackViewItemList,
  trackSelectItem,
  trackAddToCart,
  trackRemoveFromCart,
  trackViewCart,
  trackBeginCheckout,
  trackAddShippingInfo,
  trackAddPaymentInfo,
  trackPurchase,
  trackSearch,
  trackAddToWishlist,
  trackRemoveFromWishlist,
  trackSignUp,
  trackLogin,
  trackShare,
  trackFilter,
  trackSort,
  trackNewsletterSubscription,
  trackContactForm,
  trackFormSubmission,
  trackOutboundLink,
  trackCustomEvent
}; 